import {DOCUMENT} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {CrudActions} from '@application/helper/grid-action-buttons-helper/crud-actions.interface';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {BreakTime} from '@domain/calendar/pattern/break-time';
import {PatternShift} from '@domain/calendar/pattern/pattern-shift';
import {Shift} from '@domain/calendar/shift/shift';
import {PatternShiftRowData} from '@presentation/pages/calendar/pattern/add/pattern-shift-row-data.interface';
import {SchedulePreviewRowData} from '@presentation/pages/calendar/schedule/add/schedule/schedule-preview-row-data.interface';
import {ShiftSelectionService} from '@presentation/pages/calendar/shift/shift-selection.service';
import {
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  CustomCellEditorComponent,
  CustomCellEditorParams,
  CustomCellEditorRendererComponent,
  CustomCellEditorRendererParams,
  FormValidationHelper,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  NavigationHelperPrototypeService,
  ScheduleItem,
  SuffixIconCellRendererComponent,
  SuffixIconCellRendererParams,
  timeValidator,
  TranslateService,
  Unit,
  UuidUtils,
  Validation
} from '@vdw/angular-component-library';
import {ColDef, EditableCallbackParams, GridApi, GridReadyEvent, ICellRendererParams, ITooltipParams} from 'ag-grid-community';
import {Duration} from 'luxon';
import {takeUntil} from 'rxjs';
import {AddPatternShiftNavigationData} from './add-pattern-shift-navigation-data.interface';
import {BreakRowData} from './break-row-data.interface';
import {BreakForm, PatternShiftForm} from './pattern-shift.form';

@Component({
  templateUrl: 'add-pattern-shift-dialog.component.html',
  styleUrl: 'add-pattern-shift-dialog.component.scss'
})
export class AddPatternShiftDialogComponent extends BaseComponent implements OnInit {
  public readonly SHIFT_TRANSLATION_KEY = 'CALENDAR.SHIFT.SHIFT';
  public breaksConfig: CrudOverviewDataConfig<BreakRowData>;
  public addPatternShiftForm: PatternShiftForm = new FormGroup({
    shift: new FormControl<Shift>(null, Validators.required),
    startTime: new FormControl<string>(null, Validators.required),
    endTime: new FormControl<string>(null, Validators.required),
    breaks: new FormArray([])
  });

  private startTime: Duration;
  private rowData: PatternShiftRowData | SchedulePreviewRowData;
  private previousDayEndTime: Duration;
  private nextDayStartTime: Duration;
  private gridApi: GridApi;

  private get breaks(): FormArray<BreakForm> {
    return this.addPatternShiftForm.controls.breaks;
  }

  public constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<BreakRowData>,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly shiftSelectionService: ShiftSelectionService,
    private readonly translate: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    const addPatternShiftNavigationData = this.navigationHelper.getCurrentEntryData<AddPatternShiftNavigationData>();
    this.startTime = addPatternShiftNavigationData.startTime;
    this.rowData = addPatternShiftNavigationData.rowData;
    this.previousDayEndTime = addPatternShiftNavigationData.previousDayEndTime;
    this.nextDayStartTime = addPatternShiftNavigationData.nextDayStartTime;
    this.setValidators();
    this.setValueChanges();
    this.addBreak();
    this.setBreaksConfig();
  }

  public selectShift(): void {
    const field = this.addPatternShiftForm.controls.shift;
    this.shiftSelectionService
      .openContentSwitcherDialog(field.value)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((shift: Shift[]) => {
        if (!AssertionUtils.isNullOrUndefined(shift)) {
          field.setValue(shift[0]);
        }
      });
  }

  public addBreak(): void {
    const newBreak = new FormGroup({id: new FormControl<number | string>(UuidUtils.generateV4Uuid()), startTime: new FormControl<string>(null), duration: new FormControl<number>(null)});
    newBreak.controls.startTime.setValidators([timeValidator(24), this.breakStartTimeValidator(newBreak.value.id)]);
    newBreak.controls.duration.setValidators([Validators.min(1), this.breakDurationValidator(newBreak.value.id)]);
    newBreak.controls.startTime.statusChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => this.updateBreaksValidity());
    newBreak.controls.duration.statusChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => this.updateBreaksValidity());

    this.breaks.push(newBreak);
    this.gridApi?.applyTransaction({add: [this.breaks.at(this.breaks.length - 1).value]});
  }

  public addPatternShift(): void {
    const isValid = new FormValidationHelper().checkForms([this.addPatternShiftForm], this.document);
    if (isValid) {
      this.navigationHelper.navigateBack([this.getCurrentPatternShift()]);
    }
  }

  public cancel(): void {
    this.navigationHelper.navigateBack();
  }

  private setValidators(): void {
    this.addPatternShiftForm.controls.startTime.addValidators(this.overlappingShiftsValidator('start'));
    this.addPatternShiftForm.controls.endTime.addValidators(this.overlappingShiftsValidator('end'));
  }

  private setValueChanges(): void {
    this.addPatternShiftForm.controls.startTime.statusChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      setTimeout(() => this.addPatternShiftForm.controls.endTime.updateValueAndValidity({emitEvent: false}));
      this.updateBreaksValidity();
    });
    this.addPatternShiftForm.controls.endTime.statusChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      setTimeout(() => this.addPatternShiftForm.controls.startTime.updateValueAndValidity({emitEvent: false}));
      this.updateBreaksValidity();
    });
  }

  private overlappingShiftsValidator(formControlName: 'start' | 'end'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const [newStart, newEnd] = this.getStartAndEndTimeFromMidnight(
        this.startTime,
        formControlName === 'start' ? control.value : this.addPatternShiftForm.value.startTime,
        formControlName === 'end' ? control.value : this.addPatternShiftForm.value.endTime
      );

      const overlappingShifts =
        this.rowData.scheduleItems.some((scheduleItem: ScheduleItem) => newStart < scheduleItem.endTime && newEnd > scheduleItem.startTime) ||
        newStart < this.previousDayEndTime ||
        newEnd > this.nextDayStartTime;

      return overlappingShifts ? {overlappingShifts: true} : null;
    };
  }

  private setBreaksConfig(): void {
    this.breaksConfig = this.builderFactoryService
      .getBuilder()
      .withDefaultButtons({
        crudOverviewButtonConfigs: this.gridActionButtonsHelper.getActionButtonsForGridSelection({
          requiredPermissionToEdit: NavigationUtils.getNavigationData(NavigationId.EDIT_PATTERN).requiredPermission,
          withDelete: (grid: GridButtonClickedEvent) => this.deleteBreak(grid),
          allowDeleteMultiple: true
        } as CrudActions)
      })
      .withGridOptions(this.getBreakColumnDefs(), GridIdentifier.SHIFT_BREAK, this.breaks.value as BreakRowData[], (builder: GridOptionsBuilder) =>
        builder
          .withOnGridReady((event: GridReadyEvent) => (this.gridApi = event.api))
          .withIsRowSelectable(() => false, false)
          .build()
      )
      .build();
  }

  private deleteBreak({rowNode}: GridButtonClickedEvent): void {
    this.breaks.removeAt(this.breaks.controls.findIndex((breakForm: BreakForm) => breakForm === this.getBreakFormById(rowNode.data.id)));
    this.gridApi.applyTransaction({remove: [rowNode.data]});
    if (this.gridApi.getRenderedNodes().length === 0) {
      this.addBreak();
    }
  }

  private getBreakColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('GENERAL.DATETIME.START_TIME')
        .withColIdAndField('startTime', true)
        .withEditable(
          true,
          CustomCellEditorComponent,
          (params: EditableCallbackParams) =>
            ({
              notation: 24,
              placeholder: '08:00',
              formControl: this.getBreakFormById(params.data.id).controls.startTime,
              validations: [
                new Validation(null, 'valueRequired', this.translate.instant('GENERAL.ERRORS.OBJECT_IS_REQUIRED', {object: this.translate.instant('GENERAL.DATETIME.START_TIME')})),
                new Validation(null, 'invalidTimeFormat', 'ANGULAR_COMPONENT_LIBRARY.TIME_PICKER.INVALID_FORMAT'),
                new Validation(null, 'invalidStartTime', 'CALENDAR.ERROR.START_TIME_NOT_IN_RANGE_SHIFT'),
                new Validation(null, 'overlappingBreaks', this.translate.instant('CALENDAR.ERROR.OBJECT_CANT_OVERLAP', {object: this.translate.instant('CALENDAR.BREAK', {count: 2})}))
              ]
            }) as CustomCellEditorParams,
          true
        )
        .withCellRenderer(SuffixIconCellRendererComponent, {icon: 'solid-nest-clock-farsight-analog', placeholder: '08:00'} as SuffixIconCellRendererParams)
        .withOnCellClicked()
        .withSuppressHeaderMenuButton()
        .withoutFilter()
        .withFlex()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('GENERAL.DATETIME.DURATION')
        .withColIdAndField('duration')
        .withRightAlignment()
        .withEditable(
          true,
          CustomCellEditorComponent,
          (params: EditableCallbackParams) =>
            ({
              min: 1,
              unit: Unit.MINUTES,
              placeholder: '15',
              formControl: this.getBreakFormById(params.data.id).controls.duration,
              validations: [
                new Validation(null, 'valueRequired', this.translate.instant('GENERAL.ERRORS.OBJECT_IS_REQUIRED', {object: this.translate.instant('GENERAL.DATETIME.DURATION')})),
                new Validation(null, 'invalidDuration', 'CALENDAR.ERROR.DURATION_EXCEEDS_END_SHIFT'),
                new Validation(null, 'overlappingBreaks', this.translate.instant('CALENDAR.ERROR.OBJECT_CANT_OVERLAP', {object: this.translate.instant('CALENDAR.BREAK', {count: 2})}))
              ]
            }) as CustomCellEditorParams
        )
        .withCellRenderer(
          CustomCellEditorRendererComponent,
          (params: ICellRendererParams): CustomCellEditorRendererParams =>
            ({
              placeholder: '15',
              unit: Unit.MINUTES,
              value: params.value
            }) as CustomCellEditorRendererParams
        )
        .withTooltipValueGetter((params: ITooltipParams) => (AssertionUtils.isNullOrUndefined(params.value) ? null : `${params.value} ${Unit.MINUTES}`))
        .withOnCellClicked()
        .withSuppressHeaderMenuButton()
        .withoutFilter()
        .withFlex()
        .build()
    ];
  }

  private breakStartTimeValidator(id: number | string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const breakDuration = this.getBreakFormById(id).controls.duration;
      if (AssertionUtils.isNullOrWhitespace(control.value) && !AssertionUtils.isNullOrUndefined(breakDuration.value) && breakDuration.valid) {
        return {valueRequired: true};
      }

      if (AssertionUtils.isNullOrWhitespace(control.value) || this.addPatternShiftForm.controls.startTime.invalid || this.addPatternShiftForm.controls.endTime.invalid) {
        return null;
      }

      const [startTime, endTime] = this.getStartAndEndTimeFromMidnight(this.startTime, this.addPatternShiftForm.value.startTime, this.addPatternShiftForm.value.endTime);
      const breakStartTime = this.getBreakStartTimeFromMidnight(control.value, startTime);
      if (breakStartTime >= endTime) {
        return {invalidStartTime: true};
      }

      return this.isBreakOverlapping(id, control.value, breakDuration.value) ? {overlappingBreaks: true} : null;
    };
  }

  private breakDurationValidator(id: number | string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const breakStartTime = this.getBreakFormById(id).controls.startTime;
      if (AssertionUtils.isNullOrUndefined(control.value) && !AssertionUtils.isNullOrWhitespace(breakStartTime.value) && breakStartTime.valid) {
        return {valueRequired: true};
      }

      if (
        AssertionUtils.isNullOrUndefined(control.value) ||
        AssertionUtils.isNullOrWhitespace(breakStartTime.value) ||
        this.addPatternShiftForm.controls.startTime.invalid ||
        this.addPatternShiftForm.controls.endTime.invalid
      ) {
        return null;
      }

      const [startTime, endTime] = this.getStartAndEndTimeFromMidnight(this.startTime, this.addPatternShiftForm.value.startTime, this.addPatternShiftForm.value.endTime);
      const breakEndTime = this.getBreakStartTimeFromMidnight(breakStartTime.value, startTime).plus({minutes: control.value});
      if (breakEndTime > endTime) {
        return {invalidDuration: true};
      }

      return this.isBreakOverlapping(id, breakStartTime.value, control.value) ? {overlappingBreaks: true} : null;
    };
  }

  private isBreakOverlapping(id: number | string, startTime: string, duration: number): boolean {
    const currentStart = Duration.fromISOTime(startTime);
    const currentEnd = currentStart.plus({minutes: duration});
    return this.breaks.controls
      .filter((nextBreak: BreakForm) => nextBreak.value.id !== id)
      .some((nextBreak: BreakForm) => {
        const nextStart = Duration.fromISOTime(nextBreak.value.startTime);
        const nextEnd = nextStart.plus({minutes: nextBreak.value.duration});
        if (currentStart.equals(nextStart) || (currentStart < nextEnd && currentEnd > nextStart)) {
          return true;
        }
        return false;
      });
  }

  private updateBreaksValidity(): void {
    if (!AssertionUtils.isNullOrUndefined(this.gridApi)) {
      setTimeout(() =>
        this.breaks.controls.forEach((breakForm: BreakForm) => {
          breakForm.controls.startTime.updateValueAndValidity({emitEvent: false});
          breakForm.controls.duration.updateValueAndValidity({emitEvent: false});
          const data = this.gridApi.getRowNode(breakForm.value.id.toString()).data;
          data.errors = {startTime: breakForm.controls.startTime.invalid, duration: breakForm.controls.duration.invalid};
          this.gridApi.refreshCells();
        })
      );
    }
  }

  private getCurrentPatternShift(): PatternShift {
    const [startTime, endTime] = this.getStartAndEndTimeFromMidnight(this.startTime, this.addPatternShiftForm.value.startTime, this.addPatternShiftForm.value.endTime);
    let breakTimes: BreakTime[] = [];
    this.breaks.controls.forEach((breakForm: BreakForm) => {
      if (!AssertionUtils.isNullOrWhitespace(breakForm.value.startTime) && !AssertionUtils.isNullOrUndefined(breakForm.value.duration)) {
        breakTimes.push(new BreakTime(breakForm.value.id, this.getBreakStartTimeFromMidnight(breakForm.value.startTime, startTime), breakForm.value.duration));
      }
    });
    return new PatternShift(UuidUtils.generateV4Uuid(), 'day' in this.rowData ? this.rowData.day - 1 : null, this.addPatternShiftForm.value.shift, startTime, endTime, breakTimes);
  }

  private getStartAndEndTimeFromMidnight(patternStartTime: Duration, startTime: string, endTime: string): Duration[] {
    let start = Duration.fromISOTime(startTime);
    start = start < patternStartTime ? start.plus({days: 1}) : start;
    let end = Duration.fromISOTime(endTime).plus({days: start.isValid ? start.days : 0});
    end = end <= start ? end.plus({days: 1}) : end;

    return [start, end];
  }

  private getBreakStartTimeFromMidnight(breakStartTime: string, shiftStartTime: Duration): Duration {
    let start = Duration.fromISOTime(breakStartTime).plus({days: shiftStartTime.days});
    return start < shiftStartTime ? start.plus({days: 1}) : start;
  }

  private getBreakFormById(id: number | string): BreakForm {
    return this.breaks.at(this.breaks.value.findIndex((data: BreakRowData) => data.id === id));
  }
}
